import { yupResolver } from '@hookform/resolvers/yup'
import { Alert, Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { SuccessCheck } from 'components/core/action-icons/success-check'
import { C1SelectCard } from 'components/core/card/C1SelectCard'
import { CIDRChipInput } from 'components/core/chipinput'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { TextField } from 'components/core/textfield'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useAlert } from 'hooks/useAlert'
import { Shield } from 'lucide-react'
import type { WorkloadFederationProvider } from 'pbts/c1/api/workload_federation/v1/workload_federation'
import {
    WorkloadFederationServiceCreateProviderRequest,
    WorkloadFederationServiceCreateTrustRequest,
    WorkloadFederationServiceListProvidersRequest,
} from 'pbts/c1/api/workload_federation/v1/workload_federation'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { createProviderApi, createTrustApi, listProvidersApi } from 'redux_toolkit/workloadFederation'
import * as yup from 'yup'
import { CodeSnippet } from './federation/CodeSnippet'
import { SourceTrustFields } from './federation/SourceTrustFields'
import { WizardStepIndicator } from './federation/WizardStepIndicator'
import {
    getPresetForProvider,
    WORKLOAD_SOURCE_PRESETS,
    type WorkloadSourcePreset,
} from './federation/workloadSourcePresets'

type SetupFederationWizardProps = ClosableProps & {
    servicePrincipalId?: string
    tenantDomain?: string
    mode?: 'full' | 'provider_only'
}

type WizardStep = 'choose_provider' | 'create_provider' | 'configure_trust' | 'success' | 'provider_success'

// ---------------------------------------------------------------------------
// Choose Provider Step
// ---------------------------------------------------------------------------

type ChooseProviderStepProps = {
    providers: readonly WorkloadFederationProvider[]
    onUseExisting: (provider: WorkloadFederationProvider) => void
    onCreateNew: (preset: WorkloadSourcePreset) => void
    showExisting?: boolean
}

const ChooseProviderStep: FC<ChooseProviderStepProps> = ({ providers, onUseExisting, onCreateNew, showExisting = true }) => {
    const [selectedPreset, setSelectedPreset] = useState<WorkloadSourcePreset | null>(null)

    return <Stack spacing={3}>
        {/* Section A: Existing providers */}
        {showExisting && providers.length > 0 && (
            <Box>
                <Typography variant="h6" pb={1}>
                    <FormattedMessage id="wf_wizard_existing_providers" defaultMessage="Your providers" />
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><FormattedMessage id="wf_wizard_provider_name" defaultMessage="Name" /></TableCell>
                            <TableCell><FormattedMessage id="wf_wizard_provider_issuer" defaultMessage="Issuer URL" /></TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {providers.map((p) => (
                            <TableRow key={p.id} hover>
                                <TableCell>
                                    <Typography variant="body2">{p.displayName}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="monospace" noWrap sx={{ maxWidth: 250 }}>
                                        {p.issuerUrl}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <C1Button
                                        variant="outlined"
                                        onClick={(): void => onUseExisting(p)}
                                    >
                                        <FormattedMessage id="wf_wizard_use" defaultMessage="Use" />
                                    </C1Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        )}

        {/* Section B: Create new provider */}
        <Box>
            <Typography variant="h6" pb={1}>
                <FormattedMessage id="wf_wizard_create_new" defaultMessage="Create new provider" />
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                {WORKLOAD_SOURCE_PRESETS.map((preset) => (
                    <C1SelectCard
                        key={preset.wellKnownProvider}
                        title={preset.displayName}
                        description={preset.description}
                        icon={preset.iconKey === 'Custom'
                            ? <C1Icon icon={Shield} />
                            : undefined
                        }
                        isSelected={selectedPreset?.wellKnownProvider === preset.wellKnownProvider}
                        onClick={(): void => {
                            setSelectedPreset(preset)
                            onCreateNew(preset)
                        }}
                    />
                ))}
            </Box>
        </Box>
    </Stack>
}

// ---------------------------------------------------------------------------
// Create Provider Step
// ---------------------------------------------------------------------------

type CreateProviderStepProps = {
    preset: WorkloadSourcePreset
    onBack: () => void
    onCreated: (provider: WorkloadFederationProvider) => void
}

const createProviderSchema = yup.object().shape({
    displayName: yup.string()
        .required('Display name is required')
        .trim()
        .min(1)
        .max(512),
    issuerUrl: yup.string()
        .required('Issuer URL is required')
        .trim()
        .test('https', 'Issuer URL must use HTTPS', (value): boolean => !!value?.startsWith('https://')),
    description: yup.string()
        .trim()
        .max(4096)
        .default(''),
})

const CreateProviderStep: FC<CreateProviderStepProps> = ({ preset, onBack, onCreated }) => {
    const intl = useIntl()
    const toast = useAlert()
    const { mutate: createProvider, inProgress, error, data: createdProvider } = createProviderApi()

    // When provider is created, forward to parent.
    useEffect(() => {
        if (createdProvider) {
            onCreated(createdProvider)
        }
    }, [createdProvider, onCreated])

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(createProviderSchema),
        mode: 'onChange',
        defaultValues: {
            displayName: preset.providerDisplayName,
            issuerUrl: preset.issuerUrlDefault,
            description: '',
        },
    })

    const onSubmit = useCallback(async (data: yup.InferType<typeof createProviderSchema>): Promise<void> => {
        if (inProgress) return
        try {
            await createProvider(WorkloadFederationServiceCreateProviderRequest.create({
                displayName: data.displayName.trim(),
                issuerUrl: data.issuerUrl.trim(),
                description: data.description?.trim() ?? '',
                wellKnownProvider: preset.wellKnownProvider,
            }))
        } catch (err) {
            toast(intl.formatMessage({
                id: 'wf_wizard_create_provider_error',
                defaultMessage: 'Failed to create provider: {message}',
            }, {
                message: (err as RpcError).message,
            }), 'error')
        }
    }, [createProvider, preset.wellKnownProvider, inProgress, toast, intl])

    return <>
        <APIError err={error} />
        <Stack spacing={2}>
            <Typography variant="h6">
                <FormattedMessage
                    id="wf_wizard_create_provider_title"
                    defaultMessage="Create {name} provider"
                    values={{ name: preset.displayName }}
                />
            </Typography>
            <TextField
                label={<FormattedMessage id="wf_wizard_provider_display_name" defaultMessage="Display name" />}
                {...register('displayName')}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
                fullWidth
            />
            <TextField
                label={<FormattedMessage id="wf_wizard_provider_issuer_url" defaultMessage="Issuer URL" />}
                {...register('issuerUrl')}
                error={!!errors.issuerUrl}
                helperText={errors.issuerUrl?.message}
                fullWidth
                disabled={!preset.issuerUrlEditable}
                placeholder="https://"
            />
            <TextField
                label={<FormattedMessage id="wf_wizard_provider_description" defaultMessage="Description" />}
                {...register('description')}
                error={!!errors.description}
                helperText={errors.description?.message}
                fullWidth
                multiline
                rows={2}
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <C1Button onClick={onBack} variant="outlined">
                    <FormattedMessage id="wf_wizard_back" defaultMessage="Back" />
                </C1Button>
                <C1Button onClick={handleSubmit(onSubmit)} disabled={inProgress} variant="contained">
                    <FormattedMessage id="wf_wizard_create_provider" defaultMessage="Create" />
                </C1Button>
            </Stack>
        </Stack>
    </>
}

// ---------------------------------------------------------------------------
// Configure Trust Step
// ---------------------------------------------------------------------------

type ConfigureTrustStepProps = {
    provider: WorkloadFederationProvider
    preset: WorkloadSourcePreset
    servicePrincipalId: string
    onBack: () => void
    onCreated: (clientId: string) => void
}

const ConfigureTrustStep: FC<ConfigureTrustStepProps> = ({ provider, preset, servicePrincipalId, onBack, onCreated }) => {
    const intl = useIntl()
    const toast = useAlert()
    const { mutate: createTrust, inProgress, error, data: createdTrust } = createTrustApi()

    // When trust is created, forward client ID to parent.
    useEffect(() => {
        if (createdTrust?.clientId) {
            onCreated(createdTrust.clientId)
        }
    }, [createdTrust, onCreated])

    // Build dynamic default values from preset fields.
    const defaultValues = useMemo(() => {
        const values: Record<string, string | string[]> = {
            displayName: '',
            conditionExpression: '',
            allowSourceCidrs: [],
        }
        for (const field of preset.fields) {
            values[field.name] = field.default ?? ''
        }
        return values
    }, [preset.fields])

    const { register, handleSubmit, control, formState: { errors }, watch, setValue } = useForm({
        mode: 'onChange',
        defaultValues,
    })

    const onSubmit = useCallback(async (data: Record<string, string | string[]>): Promise<void> => {
        if (inProgress) return

        const displayName = (data.displayName as string) ?? ''
        const conditionExpression = (data.conditionExpression as string) ?? ''

        if (!displayName.trim() || !conditionExpression.trim()) {
            return
        }

        try {
            const cidrs = (data.allowSourceCidrs as string[] | undefined) ?? []
            await createTrust(WorkloadFederationServiceCreateTrustRequest.create({
                servicePrincipalId,
                providerId: provider.id,
                displayName: displayName.trim(),
                conditionExpression: conditionExpression.trim(),
                allowSourceCidrs: cidrs,
            }))
        } catch (err) {
            toast(intl.formatMessage({
                id: 'wf_wizard_create_trust_error',
                defaultMessage: 'Failed to create trust: {message}',
            }, {
                message: (err as RpcError).message,
            }), 'error')
        }
    }, [createTrust, servicePrincipalId, provider.id, inProgress, toast, intl])

    return <>
        <APIError err={error} />
        <Stack spacing={3}>
            <Alert severity="info" icon={false}>
                <Typography variant="body2">
                    <FormattedMessage
                        id="wf_wizard_trust_provider_context"
                        defaultMessage="Provider: {name}"
                        values={{ name: provider.displayName }}
                    />
                </Typography>
            </Alert>

            <SourceTrustFields
                preset={preset}
                control={control}
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors as Record<string, { message?: string }>}
            />

            {/* Advanced section: IP allowlist */}
            <Box>
                <Typography variant="body2" color="text.secondary" pb={0.5}>
                    <FormattedMessage id="wf_wizard_trust_cidr_label" defaultMessage="IP allowlist (optional)" />
                </Typography>
                <Controller
                    name="allowSourceCidrs"
                    control={control}
                    render={({ field }): React.ReactElement =>
                        <CIDRChipInput
                            aria-label={intl.formatMessage({
                                id: 'wf_wizard_trust_cidr_aria',
                                defaultMessage: 'IP allowlist',
                            })}
                            editable
                            value={(field.value as string[]) ?? []}
                            setValue={(values): void => field.onChange(values)}
                        />
                    }
                />
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <C1Button onClick={onBack} variant="outlined">
                    <FormattedMessage id="wf_wizard_back" defaultMessage="Back" />
                </C1Button>
                <C1Button onClick={handleSubmit(onSubmit)} disabled={inProgress} variant="contained">
                    <FormattedMessage id="wf_wizard_create_trust" defaultMessage="Create trust" />
                </C1Button>
            </Stack>
        </Stack>
    </>
}

// ---------------------------------------------------------------------------
// Success Step
// ---------------------------------------------------------------------------

type SuccessStepProps = {
    clientId: string
    preset: WorkloadSourcePreset
    tenantDomain: string
}

const SuccessStep: FC<SuccessStepProps> = ({ clientId, preset, tenantDomain }) => {
    const snippets = preset.successSnippets({ tenantDomain, clientId })

    return <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center">
            <SuccessCheck />
            <Typography variant="h6">
                <FormattedMessage id="wf_wizard_trust_created" defaultMessage="Trust created successfully" />
            </Typography>
        </Stack>

        <Box>
            <Typography variant="body2" color="text.secondary" pb={1}>
                <FormattedMessage id="wf_wizard_client_id_label" defaultMessage="Client ID (for token exchange)" />
            </Typography>
            <C1CopyText value={clientId} typographyVariant="monospace" />
        </Box>

        {snippets.length > 0 && (
            <Box>
                <Typography variant="body2" color="text.secondary" pb={1}>
                    <FormattedMessage id="wf_wizard_setup_instructions" defaultMessage="Setup instructions" />
                </Typography>
                <Stack spacing={2}>
                    {snippets.map((snippet, i) => (
                        <CodeSnippet key={i} snippet={snippet} />
                    ))}
                </Stack>
            </Box>
        )}
    </Stack>
}

// ---------------------------------------------------------------------------
// Provider Success Step (provider_only mode)
// ---------------------------------------------------------------------------

type ProviderSuccessStepProps = {
    provider: WorkloadFederationProvider
}

const ProviderSuccessStep: FC<ProviderSuccessStepProps> = ({ provider }) => {
    return <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center">
            <SuccessCheck />
            <Typography variant="h6">
                <FormattedMessage id="wf_wizard_provider_created" defaultMessage="Provider created" />
            </Typography>
        </Stack>

        <Box>
            <Typography variant="body2" color="text.secondary" pb={1}>
                <FormattedMessage id="wf_wizard_provider_name_label" defaultMessage="Display name" />
            </Typography>
            <Typography>{provider.displayName}</Typography>
        </Box>

        <Box>
            <Typography variant="body2" color="text.secondary" pb={1}>
                <FormattedMessage id="wf_wizard_provider_issuer_label" defaultMessage="Issuer URL" />
            </Typography>
            <C1CopyText value={provider.issuerUrl} typographyVariant="monospace" />
        </Box>
    </Stack>
}

// ---------------------------------------------------------------------------
// Main Wizard
// ---------------------------------------------------------------------------

export const SetupFederationWizard: FC<SetupFederationWizardProps> = ({ close, servicePrincipalId, tenantDomain, mode = 'full' }) => {
    const [step, setStep] = useState<WizardStep>('choose_provider')
    const [selectedProvider, setSelectedProvider] = useState<WorkloadFederationProvider | null>(null)
    const [selectedPreset, setSelectedPreset] = useState<WorkloadSourcePreset | null>(null)
    const [createdClientId, setCreatedClientId] = useState<string>('')

    // Fetch existing providers for step 1.
    const providerRequest = useMemo(() => WorkloadFederationServiceListProvidersRequest.create({}), [])
    const { data: providersData } = listProvidersApi.useListProviders(providerRequest, { notify: false, autoReload: true })
    const providers = useMemo(() => (providersData ?? []).flat(), [providersData])

    // Resolve preset from selected provider.
    const activePreset = selectedPreset ?? (selectedProvider
        ? getPresetForProvider(selectedProvider.wellKnownProvider)
        : null)

    const handleUseExisting = useCallback((provider: WorkloadFederationProvider): void => {
        setSelectedProvider(provider)
        setSelectedPreset(getPresetForProvider(provider.wellKnownProvider))
        setStep('configure_trust')
    }, [])

    const handleCreateNew = useCallback((preset: WorkloadSourcePreset): void => {
        setSelectedPreset(preset)
        setStep('create_provider')
    }, [])

    const handleProviderCreated = useCallback((provider: WorkloadFederationProvider): void => {
        setSelectedProvider(provider)
        if (mode === 'provider_only') {
            setStep('provider_success')
        } else {
            setStep('configure_trust')
        }
    }, [mode])

    const handleTrustCreated = useCallback((clientId: string): void => {
        setCreatedClientId(clientId)
        setStep('success')
    }, [])

    const handleBackToChoose = useCallback((): void => {
        setSelectedProvider(null)
        setSelectedPreset(null)
        setStep('choose_provider')
    }, [])

    // Step indicator for full mode (2 steps: Select provider, Configure trust).
    const wizardSteps = useMemo(() => [
        { label: <FormattedMessage id="wf_wizard_step_provider" defaultMessage="Select provider" /> },
        { label: <FormattedMessage id="wf_wizard_step_trust" defaultMessage="Configure trust" /> },
    ], [])

    const activeStepIndex = useMemo((): number => {
        switch (step) {
            case 'choose_provider':
            case 'create_provider':
                return 0
            case 'configure_trust':
                return 1
            default:
                return -1
        }
    }, [step])

    // Drawer header based on step and mode.
    const headerTitle = useMemo((): React.ReactNode => {
        if (mode === 'provider_only') {
            if (step === 'provider_success') {
                return <FormattedMessage id="wf_wizard_title_provider_created" defaultMessage="Provider created" />
            }
            return <FormattedMessage id="wf_wizard_title_add_provider" defaultMessage="Add provider" />
        }
        switch (step) {
            case 'choose_provider':
                return <FormattedMessage id="wf_wizard_title_choose" defaultMessage="Setup federation" />
            case 'create_provider':
                return <FormattedMessage id="wf_wizard_title_create_provider" defaultMessage="Setup federation" />
            case 'configure_trust':
                return <FormattedMessage id="wf_wizard_title_configure" defaultMessage="Setup federation" />
            case 'success':
                return <FormattedMessage id="wf_wizard_title_success" defaultMessage="Trust created" />
            default:
                return <FormattedMessage id="wf_wizard_title" defaultMessage="Setup federation" />
        }
    }, [step, mode])

    const isSuccessStep = step === 'success' || step === 'provider_success'

    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <Stack spacing={0}>
                {headerTitle}
                {mode === 'full' && activeStepIndex >= 0 && (
                    <WizardStepIndicator steps={wizardSteps} activeStep={activeStepIndex} />
                )}
            </Stack>
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            {step === 'choose_provider' && (
                <ChooseProviderStep
                    providers={providers}
                    onUseExisting={handleUseExisting}
                    onCreateNew={handleCreateNew}
                    showExisting={mode === 'full'}
                />
            )}
            {step === 'create_provider' && selectedPreset && (
                <CreateProviderStep
                    preset={selectedPreset}
                    onBack={handleBackToChoose}
                    onCreated={handleProviderCreated}
                />
            )}
            {step === 'configure_trust' && selectedProvider && activePreset && servicePrincipalId && (
                <ConfigureTrustStep
                    provider={selectedProvider}
                    preset={activePreset}
                    servicePrincipalId={servicePrincipalId}
                    onBack={handleBackToChoose}
                    onCreated={handleTrustCreated}
                />
            )}
            {step === 'success' && activePreset && (
                <SuccessStep
                    clientId={createdClientId}
                    preset={activePreset}
                    tenantDomain={tenantDomain ?? ''}
                />
            )}
            {step === 'provider_success' && selectedProvider && (
                <ProviderSuccessStep provider={selectedProvider} />
            )}
        </C1DrawerInner.Content>
        {isSuccessStep && (
            <C1DrawerInner.Footer>
                <C1Button onClick={close} variant="contained">
                    <FormattedMessage id="wf_wizard_done" defaultMessage="Done" />
                </C1Button>
            </C1DrawerInner.Footer>
        )}
    </C1DrawerInner>
}
